import { getNonDeletedElements } from "@excalidraw/element";

import { CODES, KEYS, arrayToMap, getShortcutKey } from "@excalidraw/common";

import { updateFrameMembershipOfSelectedElements } from "@excalidraw/element";

import {
  distributeElements,
  reapplyAutoLayoutToAllFrames,
} from "@excalidraw/element";

import { CaptureUpdateAction } from "@excalidraw/element";

import type { ExcalidrawElement } from "@excalidraw/element/types";

import type { Distribution } from "@excalidraw/element";

import { ToolButton } from "../components/ToolButton";
import {
  DistributeHorizontallyIcon,
  DistributeVerticallyIcon,
} from "../components/icons";

import { t } from "../i18n";

import { isSomeElementSelected } from "../scene";

import { register } from "./register";

import type { AppClassProperties, AppState } from "../types";

const enableActionGroup = (appState: AppState, app: AppClassProperties) => {
  const selectedElements = app.scene.getSelectedElements(appState);
  return selectedElements.length > 1;
};

const distributeSelectedElements = (
  elements: readonly ExcalidrawElement[],
  appState: Readonly<AppState>,
  app: AppClassProperties,
  distribution: Distribution,
) => {
  const selectedElements = app.scene.getSelectedElements(appState);

  const updatedElements = distributeElements(
    selectedElements,
    app.scene.getNonDeletedElementsMap(),
    distribution,
  );

  const updatedElementsMap = arrayToMap(updatedElements);

  return updateFrameMembershipOfSelectedElements(
    elements.map((element) => updatedElementsMap.get(element.id) || element),
    appState,
    app,
  );
};

// Helper function to trigger auto layout rerendering after distribution actions
// This ensures that child elements within frames are properly repositioned
const triggerAutoLayoutRerendering = (app: AppClassProperties) => {
  // Apply the same layered rerendering pattern used to fix previous auto layout rerender issues
  setTimeout(() => {
    const allElements = app.scene.getElementsIncludingDeleted();
    const elementsMap = app.scene.getNonDeletedElementsMap();
    const updatedElements = reapplyAutoLayoutToAllFrames(
      allElements,
      elementsMap,
    );
    if (updatedElements !== allElements) {
      app.scene.replaceAllElements(updatedElements);

      // Trigger additional auto layout rerendering for nested frames
      setTimeout(() => {
        const currentElements = app.scene.getElementsIncludingDeleted();
        const currentElementsMap = app.scene.getNonDeletedElementsMap();
        const rerenderedElements = reapplyAutoLayoutToAllFrames(
          currentElements,
          currentElementsMap,
        );
        if (rerenderedElements !== currentElements) {
          app.scene.replaceAllElements(rerenderedElements);
        }

        // Final trigger to ensure visual re-rendering
        app.scene.triggerUpdate();
      }, 0);
    } else {
      // Even if no layout changes, trigger update for visual feedback
      app.scene.triggerUpdate();
    }
  }, 0);
};

export const distributeHorizontally = register({
  name: "distributeHorizontally",
  label: "labels.distributeHorizontally",
  trackEvent: { category: "element" },
  perform: (elements, appState, _, app) => {
    // Trigger auto layout rerendering to ensure child elements within frames are repositioned
    triggerAutoLayoutRerendering(app);

    return {
      appState,
      elements: distributeSelectedElements(elements, appState, app, {
        space: "between",
        axis: "x",
      }),
      captureUpdate: CaptureUpdateAction.IMMEDIATELY,
    };
  },
  keyTest: (event) =>
    !event[KEYS.CTRL_OR_CMD] && event.altKey && event.code === CODES.H,
  PanelComponent: ({ elements, appState, updateData, app }) => (
    <ToolButton
      hidden={!enableActionGroup(appState, app)}
      type="button"
      icon={DistributeHorizontallyIcon}
      onClick={() => updateData(null)}
      title={`${t("labels.distributeHorizontally")} — ${getShortcutKey(
        "Alt+H",
      )}`}
      aria-label={t("labels.distributeHorizontally")}
      visible={isSomeElementSelected(getNonDeletedElements(elements), appState)}
    />
  ),
});

export const distributeVertically = register({
  name: "distributeVertically",
  label: "labels.distributeVertically",
  trackEvent: { category: "element" },
  perform: (elements, appState, _, app) => {
    // Trigger auto layout rerendering to ensure child elements within frames are repositioned
    triggerAutoLayoutRerendering(app);

    return {
      appState,
      elements: distributeSelectedElements(elements, appState, app, {
        space: "between",
        axis: "y",
      }),
      captureUpdate: CaptureUpdateAction.IMMEDIATELY,
    };
  },
  keyTest: (event) =>
    !event[KEYS.CTRL_OR_CMD] && event.altKey && event.code === CODES.V,
  PanelComponent: ({ elements, appState, updateData, app }) => (
    <ToolButton
      hidden={!enableActionGroup(appState, app)}
      type="button"
      icon={DistributeVerticallyIcon}
      onClick={() => updateData(null)}
      title={`${t("labels.distributeVertically")} — ${getShortcutKey("Alt+V")}`}
      aria-label={t("labels.distributeVertically")}
      visible={isSomeElementSelected(getNonDeletedElements(elements), appState)}
    />
  ),
});
